import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useRowKeys } from "@/lib/useRowKeys";
import { ArrowLeft, Download, AlertTriangle, CheckCircle2, Circle, Plus, X } from "lucide-react";
import { api, errMsg, download } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PermitStatus, fmt } from "@/pages/app/Permits";

const ACTIONS = {
  approve: ["Authorise permit", "I confirm I have verified on site that the precautions, isolations and pre-start checks are in place and the work can start safely.", true, false],
  reject: ["Return permit to author", null, true, true],
  suspend: ["Suspend permit — stop work", null, false, true],
  resume: ["Resume permit", "I confirm conditions are safe for work to resume.", true, false],
  extend: ["Extend permit", null, true, true],
  close: ["Close permit", "I confirm the work is complete and the area has been left safe, with isolations removed or handed back.", true, false],
  cancel: ["Cancel permit", null, false, false],
};

const ActionDialog = ({ act, setAct, run, name }) => {
  const [f, setF] = useState({ signature: "", note: "", confirm: false, new_valid_to: "" });
  useEffect(() => {
    setF({ signature: "", note: "", confirm: false, new_valid_to: "" });
  }, [act]);
  if (!act) return null;
  const [title, confirmText, needSig, needNote] = ACTIONS[act];
  return (
    <Dialog open onOpenChange={() => setAct(null)}><DialogContent><DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
      <div className="space-y-3">
        {act === "extend" && <div className="space-y-1"><Label>New end time</Label><Input type="datetime-local" value={f.new_valid_to} onChange={(e) => setF({ ...f, new_valid_to: e.target.value })} data-testid="extend-to" /></div>}
        {needNote && <div className="space-y-1"><Label>Reason</Label><Textarea rows={3} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="action-note" /></div>}
        {confirmText && <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1" checked={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.checked })} data-testid="action-confirm" />{confirmText}</label>}
        {needSig && <div className="space-y-1"><Label>Sign by typing your full name ({name})</Label><Input value={f.signature} onChange={(e) => setF({ ...f, signature: e.target.value })} data-testid="action-signature" /></div>}
        <Button className="w-full" variant={["suspend", "cancel"].includes(act) ? "destructive" : "default"} onClick={() => run(act, { ...f, new_valid_to: f.new_valid_to ? new Date(f.new_valid_to).toISOString() : null })} data-testid="action-submit">{title}</Button>
      </div>
    </DialogContent></Dialog>
  );
};

const Check = ({ items, kind, can, onToggle }) => (
  <ul className="space-y-1.5">{items.length === 0 && <li className="text-sm text-muted-foreground">None listed.</li>}{items.map((i, idx) => (
    <li key={idx} className="flex items-start gap-2 text-sm">
      <button disabled={!can} onClick={() => onToggle(kind, idx, !i.done)} data-testid={`${kind}-${idx}`} className="mt-0.5 disabled:cursor-default">{i.done ? <CheckCircle2 className="h-4 w-4 text-health" /> : <Circle className="h-4 w-4 text-muted-foreground" />}</button>
      <span className="flex-1">{i.item}{i.by && <span className="block text-xs text-muted-foreground">Confirmed by {i.by} · {fmt(i.at)}</span>}</span>
    </li>
  ))}</ul>
);

const ListEdit = ({ label, value, onChange, testid, obj }) => {
  const [keys, drop] = useRowKeys(value.length);
  return (
  <div className="space-y-1"><Label>{label}</Label>
    {value.map((v, i) => <div key={keys[i]} className="flex gap-2"><Input value={typeof v === "string" ? v : v.item} onChange={(e) => onChange(value.map((x, k) => (k === i ? (typeof x === "string" ? e.target.value : { ...x, item: e.target.value }) : x)))} /><button onClick={() => { drop(i); onChange(value.filter((_, k) => k !== i)); }}><X className="h-4 w-4 text-alert" /></button></div>)}
    <Button size="sm" variant="ghost" onClick={() => onChange([...value, obj ? { item: "" } : ""])} data-testid={`${testid}-add`}><Plus className="mr-1 h-3 w-3" />Add</Button></div>
  );
};

const Editor = ({ p, setP, save, authorisers }) => {
  const set = (k) => (v) => setP({ ...p, [k]: v?.target ? v.target.value : v });
  return (
    <div className="space-y-4 rounded-2xl border bg-card p-6" data-testid="permit-editor">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1 sm:col-span-2"><Label>Title</Label><Input value={p.title} onChange={set("title")} data-testid="edit-title" /></div>
        <div className="space-y-1"><Label>Location</Label><Input value={p.location} onChange={set("location")} /></div>
        <div className="space-y-1"><Label>Named authorised person</Label><select className="w-full rounded-md border bg-background px-3 py-2 text-sm" value={p.authoriser_id || ""} onChange={set("authoriser_id")} data-testid="edit-authoriser"><option value="">Not chosen</option>{authorisers.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></div>
        <div className="space-y-1 sm:col-span-2"><Label>Scope</Label><Textarea rows={3} value={p.scope} onChange={set("scope")} /></div>
      </div>
      <ListEdit label="Hazards" value={p.hazards} onChange={set("hazards")} testid="hazards" />
      <ListEdit label="Precautions" value={p.precautions} onChange={set("precautions")} testid="precautions" />
      <ListEdit label="PPE" value={p.ppe} onChange={set("ppe")} testid="ppe" />
      <ListEdit obj label="Isolations to confirm" value={p.isolations} onChange={set("isolations")} testid="isolations" />
      <ListEdit obj label="Pre-start checks" value={p.pre_start_checks} onChange={set("pre_start_checks")} testid="checks" />
      <div className="space-y-1"><Label>Emergency arrangements</Label><Textarea rows={2} value={p.emergency_arrangements} onChange={set("emergency_arrangements")} /></div>
      <p className="text-xs text-muted-foreground">Saving changes to isolations or checks clears any ticks already recorded.</p>
      <Button onClick={save} data-testid="permit-save">Save draft</Button>
    </div>
  );
};

export default function PermitView() {
  const { id } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [edit, setEdit] = useState(false);
  const [act, setAct] = useState(null);
  const [auth, setAuth] = useState([]);
  const load = () => api.get(`/permits/${id}`).then((r) => setP(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
    api.get("/permits/authorisers").then((r) => setAuth(r.data));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!p) return <Loader />;
  const run = async (action, f) => {
    try {
      const { data } = await api.post(`/permits/${id}/action`, { action, ...f });
      setP(data);
      setAct(null);
      toast.success("Permit updated");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const toggle = async (kind, index, done) => {
    try {
      await api.post(`/permits/${id}/check`, { kind, index, done });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const save = async () => {
    try {
      const { data } = await api.put(`/permits/${id}`, p);
      setP(data);
      setEdit(false);
      toast.success("Draft saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const c = p.can;
  const mine = p.created_by === user.id;
  const btns = [
    p.status === "draft" && (mine || c.edit) && ["submit", "Submit for authorisation"],
    p.status === "pending_approval" && c.authorise && ["approve", "Authorise"], p.status === "pending_approval" && c.authorise && ["reject", "Return"],
    c.suspend && ["suspend", "Suspend"], p.status === "suspended" && c.authorise && ["resume", "Resume"],
    ["active", "approved"].includes(p.status) && c.authorise && ["extend", "Extend"], c.close && ["close", "Close permit"],
    ["draft", "pending_approval", "approved"].includes(p.status) && (mine || c.edit) && ["cancel", "Cancel"],
  ].filter(Boolean);
  const open = [...p.isolations, ...p.pre_start_checks].filter((i) => !i.done).length;
  return (
    <div className="mx-auto max-w-6xl space-y-6" data-testid="permit-view">
      <Link to="/app/permits" className="inline-flex items-center gap-1 text-sm text-muted-foreground"><ArrowLeft className="h-4 w-4" />Permits</Link>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div><div className="flex items-center gap-2"><span className="font-mono text-xs text-muted-foreground">{p.ref}</span><PermitStatus s={p.status} /></div>
          <h1 className="mt-2 text-3xl font-bold">{p.title}</h1><p className="text-sm text-muted-foreground">{p.type} · {p.location || "Location not set"}</p></div>
        <div className="flex flex-wrap gap-2">
          {btns.map(([a, l]) => <Button key={a} variant={["suspend", "cancel", "reject"].includes(a) ? "outline" : "default"} onClick={() => (a === "submit" ? run("submit", {}) : setAct(a))} data-testid={`permit-${a}-btn`}>{l}</Button>)}
          {c.edit && !edit && <Button variant="outline" onClick={() => setEdit(true)} data-testid="permit-edit-btn">Edit</Button>}
          <Button variant="outline" onClick={() => download(`/permits/${id}/pdf`, `${p.ref}.pdf`)} data-testid="permit-pdf"><Download className="mr-1.5 h-4 w-4" />PDF</Button>
        </div>
      </div>
      {p.status === "pending_approval" && !c.authorise && <div className="rounded-xl border border-[#FFA000]/30 bg-[#FFA000]/10 p-3 text-sm">Waiting for {p.authoriser_name} to authorise. Work must not start yet.</div>}
      {["draft", "pending_approval"].includes(p.status) && <div className="flex items-start gap-2 rounded-xl border border-alert/30 bg-alert/5 p-3 text-sm"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-alert" />Not authorised — work must not start.{p.ai_generated && " This permit was drafted by AI: check every hazard, precaution and isolation against the actual site before submitting."}</div>}
      {p.status === "suspended" && <div className="rounded-xl border border-alert/40 bg-alert/10 p-3 text-sm font-medium text-alert">Permit suspended — stop work until the authorised person resumes it.</div>}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {edit ? <Editor p={p} setP={setP} save={save} authorisers={auth} /> : (
            <div className="space-y-5 rounded-2xl border bg-card p-6">
              <div><h2 className="font-semibold">Scope of work</h2><p className="mt-1 whitespace-pre-line text-sm">{p.scope || "—"}</p></div>
              {[["Hazards", p.hazards], ["Precautions", p.precautions], ["PPE", p.ppe]].map(([l, v]) => <div key={l}><h2 className="font-semibold">{l}</h2><ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm">{v.length ? v.map((x, i) => <li key={i}>{x}</li>) : <li className="list-none text-muted-foreground">None listed</li>}</ul></div>)}
              <div><h2 className="font-semibold">Emergency arrangements</h2><p className="mt-1 text-sm">{p.emergency_arrangements || "—"}</p></div>
              {(p.assumptions?.length > 0 || p.missing_information?.length > 0) && <div className="rounded-xl bg-[#FFA000]/10 p-4 text-sm"><p className="font-medium">Assumptions & missing information</p><ul className="mt-1 list-disc pl-5">{[...p.assumptions, ...p.missing_information].map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
            </div>
          )}
          <div className="grid gap-6 sm:grid-cols-2">
            <div className="rounded-2xl border bg-card p-5"><h2 className="mb-3 font-semibold">Isolations</h2><Check items={p.isolations} kind="isolations" can={c.check && !edit} onToggle={toggle} /></div>
            <div className="rounded-2xl border bg-card p-5"><h2 className="mb-3 font-semibold">Pre-start checks</h2><Check items={p.pre_start_checks} kind="pre_start_checks" can={c.check && !edit} onToggle={toggle} /></div>
          </div>
          {open > 0 && ["draft", "pending_approval"].includes(p.status) && <p className="text-sm text-muted-foreground" data-testid="open-checks">{open} item(s) still to confirm before the permit can be authorised.</p>}
        </div>
        <aside className="space-y-4">
          <div className="rounded-2xl border bg-card p-5 text-sm">
            <dl className="space-y-2">{[["Valid from", fmt(p.valid_from)], ["Valid to", fmt(p.valid_to)], ["Raised by", p.created_by_name], ["Authorised person", p.authoriser_name || "Not chosen"], ["Authorised", p.approved_by ? `${p.approved_by} · ${fmt(p.approved_at)}` : "No"], ["Closed", p.closed_by ? `${p.closed_by} · ${fmt(p.closed_at)}` : "—"]].map(([k, v]) => <div key={k} className="flex justify-between gap-3"><dt className="text-muted-foreground">{k}</dt><dd className="text-right font-medium">{v}</dd></div>)}</dl>
            {p.extensions?.length > 0 && <div className="mt-3 border-t pt-3"><p className="text-xs font-medium">Extensions</p>{p.extensions.map((e, i) => <p key={i} className="text-xs text-muted-foreground">{fmt(e.to)} — {e.reason} ({e.by})</p>)}</div>}
          </div>
          <div className="rounded-2xl border bg-card p-5"><h2 className="mb-3 text-sm font-semibold">Audit history</h2>
            <ol className="space-y-2 text-xs" data-testid="permit-history">{[...p.history].reverse().map((h, i) => <li key={i} className="border-l-2 border-health/40 pl-3"><p className="font-medium capitalize">{h.action.replace(/_/g, " ")}</p><p className="text-muted-foreground">{h.by} · {fmt(h.at)}</p>{h.note && <p className="text-muted-foreground">{h.note}</p>}</li>)}</ol></div>
        </aside>
      </div>
      <ActionDialog act={act} setAct={setAct} run={run} name={user.name} />
    </div>
  );
}
